import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";

import Badge from "@mui/material/Badge";
import classes from "./NotificationContainer.module.css";

import { deleteUpdateNotifications } from "../../actions/notificationActions";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import translate from "i18n/translate";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import NotificationToast, {
  NOTIFICATION_TYPE_THEME,
  detectNotificationType,
} from "./NotificationToast";

// ─── Banking / fintech palette ──────────────────────────────────────────────
const PALETTE = {
  headerBg:     "#0F172A", // slate-900
  headerText:   "#FFFFFF",
  headerMuted:  "rgba(255,255,255,0.65)",
  bodyBg:       "#FFFFFF",
  border:       "rgba(15, 23, 42, 0.08)",
  rowDivider:   "rgba(15, 23, 42, 0.06)",
  rowBgUnread:  "#F8FAFC",
  rowBgRead:    "#FFFFFF",
  rowHover:     "rgba(15, 23, 42, 0.04)",
  primary:      "#0F172A",
  secondary:    "#475569",
  muted:        "#94A3B8",
  accentBlue:   "#2563EB",
  accentRed:    "#DC2626",
};

const NotificationContainer = ({
  list,
  deleteUpdateNotifications,
  unseenNotifications,
  newNotification,
  setNewNotification,
}) => {
  const listRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewFilter, setViewFilter] = useState("all");

  useEffect(() => {
    if (list.length === 0) {
      handleMenuClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const filteredNotifications = useMemo(() => {
    if (viewFilter === "unread") {
      return list.filter((n) => n.view === "0");
    }
    return list;
  }, [list, viewFilter]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const resolveTimestamp = (n) =>
    n.created_at || n.createdAt || n.timestamp || n.time || n.date || "";

  const markAllRead = () => {
    list.forEach((n) => {
      if (n.view === "0") {
        deleteUpdateNotifications("view", n.index, 1);
      }
    });
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      keepMounted
      open={!!anchorEl}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          borderRadius: "10px",
          marginTop: "6px",
          width: 360,
          maxWidth: "92vw",
          background: PALETTE.bodyBg,
          border: `1px solid ${PALETTE.border}`,
          boxShadow:
            "0 12px 32px rgba(15, 23, 42, 0.16), 0 4px 10px rgba(15, 23, 42, 0.06)",
          fontFamily:
            'Inter,"Segoe UI",-apple-system,BlinkMacSystemFont,Roboto,sans-serif',
          "& .MuiMenu-list": { padding: 0 },
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: PALETTE.headerBg,
          color: PALETTE.headerText,
          padding: "12px 16px",
          borderRadius: "10px 10px 0 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            component="div"
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: PALETTE.headerMuted,
              lineHeight: 1,
            }}
          >
            {translate("Notifications") || "Notifications"}
          </Typography>
          <Typography
            component="div"
            sx={{
              fontSize: "0.95rem",
              fontWeight: 700,
              marginTop: "4px",
              lineHeight: 1.1,
            }}
          >
            {unseenNotifications > 0
              ? `${unseenNotifications} ${translate("Unread") || "unread"}`
              : translate("All caught up!") || "All caught up"}
          </Typography>
        </Box>

        {unseenNotifications > 0 && (
          <Button
            size="small"
            onClick={markAllRead}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: PALETTE.headerText,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.7rem",
              borderRadius: "6px",
              padding: "4px 8px",
              minWidth: 0,
              background: "rgba(255,255,255,0.08)",
              "&:hover": { background: "rgba(255,255,255,0.16)" },
            }}
          >
            {translate("Mark all read") || "Mark all read"}
          </Button>
        )}
      </Box>

      {/* ── Filter tabs ─────────────────────────────────────────────────── */}
      <Box sx={{ padding: "10px 16px 8px", background: PALETTE.bodyBg }}>
        <ToggleButtonGroup
          value={viewFilter}
          exclusive
          fullWidth
          onChange={(_, v) => v && setViewFilter(v)}
          sx={{
            background: "#F1F5F9",
            borderRadius: "8px",
            padding: "3px",
            "& .MuiToggleButton-root": {
              flex: 1,
              border: "none",
              borderRadius: "6px !important",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.72rem",
              padding: "4px 10px",
              color: PALETTE.secondary,
              minHeight: "28px",
              transition: "background 0.12s ease, color 0.12s ease",
              "&.Mui-selected": {
                background: PALETTE.bodyBg,
                color: PALETTE.primary,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.1)",
              },
              "&:hover:not(.Mui-selected)": { background: "rgba(15,23,42,0.04)" },
            },
          }}
        >
          <ToggleButton value="all">
            {translate("All") || "All"} ({list.length})
          </ToggleButton>
          <ToggleButton value="unread">
            {translate("Unread") || "Unread"} ({unseenNotifications})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ borderColor: PALETTE.rowDivider }} />

      {/* ── Notification list ────────────────────────────────────────── */}
      <Box
        sx={{
          maxHeight: "60vh",
          overflowY: "auto",
          background: PALETTE.bodyBg,
        }}
      >
        {filteredNotifications.length === 0 ? (
          <Box
            sx={{
              padding: "32px 16px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: PALETTE.muted,
              }}
            >
              <NotificationsIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: PALETTE.primary,
              }}
            >
              {viewFilter === "unread"
                ? translate("All caught up!") || "All caught up!"
                : translate("No notifications") || "No notifications"}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: PALETTE.secondary,
                maxWidth: 240,
                lineHeight: 1.4,
              }}
            >
              {viewFilter === "unread"
                ? translate("You have read everything.") ||
                  "You have read everything."
                : translate("You have no notifications yet.") ||
                  "You have no notifications yet."}
            </Typography>
          </Box>
        ) : (
          filteredNotifications.map((n, idx) => {
            const isUnread = n.view === "0";
            const ts = resolveTimestamp(n);
            const type = detectNotificationType(n);
            const theme = NOTIFICATION_TYPE_THEME[type] || NOTIFICATION_TYPE_THEME.info;
            const Icon = theme.Icon;
            const key = n.index ?? n.id ?? `${n.title || "n"}-${idx}`;

            return (
              <Box
                key={key}
                sx={{
                  position: "relative",
                  display: "flex",
                  gap: 1.25,
                  padding: "12px 14px 12px 16px",
                  background: isUnread ? PALETTE.rowBgUnread : PALETTE.rowBgRead,
                  borderBottom: `1px solid ${PALETTE.rowDivider}`,
                  cursor: "default",
                  transition: "background 0.1s ease",
                  "&:hover": { background: PALETTE.rowHover },
                  "&:last-of-type": { borderBottom: "none" },
                }}
              >
                {/* Accent stripe */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: isUnread ? theme.accent : "transparent",
                  }}
                />

                {/* Icon */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: theme.tint,
                    color: theme.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} />
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.81rem",
                        fontWeight: isUnread ? 700 : 600,
                        color: PALETTE.primary,
                        lineHeight: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      {n.title}
                    </Typography>
                    {ts && (
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: PALETTE.muted,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {ts}
                      </Typography>
                    )}
                  </Box>

                  {n.message && (
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: PALETTE.secondary,
                        lineHeight: 1.4,
                        marginTop: "2px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {n.message}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: "6px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: theme.accent,
                      }}
                    >
                      {theme.label}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 0.25 }}>
                      {isUnread && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteUpdateNotifications("view", n.index, 1);
                          }}
                          aria-label="mark read"
                          sx={{
                            width: 24,
                            height: 24,
                            color: PALETTE.secondary,
                            borderRadius: "6px",
                            "&:hover": {
                              color: PALETTE.accentBlue,
                              background: "rgba(37, 99, 235, 0.08)",
                            },
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteUpdateNotifications(
                            "delete",
                            n.index,
                            isUnread ? 1 : 0
                          );
                        }}
                        aria-label="delete"
                        sx={{
                          width: 24,
                          height: 24,
                          color: PALETTE.secondary,
                          borderRadius: "6px",
                          "&:hover": {
                            color: PALETTE.accentRed,
                            background: "rgba(220, 38, 38, 0.08)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Menu>
  );

  return (
    <>
      <div
        className={`d-flex align-items-center justify-content-center notification-badge p-2 ${
          newNotification ? classes["shake-effect"] : ""
        }`}
        ref={listRef}
        style={{
          borderRadius: "50px",
          top: 15,
          zIndex: 1001,
          cursor: "pointer",
        }}
      >
        <IconButton
          onClick={(e) => {
            handleMenuOpen(e);
            setNewNotification(false);
          }}
          aria-label="open notifications"
        >
          <Badge badgeContent={unseenNotifications} color="error">
            <NotificationsIcon fontSize="large" />
          </Badge>
        </IconButton>
        <div className="position-relative">{renderMenu}</div>
      </div>

      <NotificationToast
        list={list}
        newNotification={newNotification}
        onDismiss={() => setNewNotification(false)}
      />
    </>
  );
};

const mapStateToProps = ({
  notification: { singleNotificationLoading, unseenNotifications },
}) => ({
  singleNotificationLoading,
  unseenNotifications,
});

export default connect(mapStateToProps, { deleteUpdateNotifications })(
  NotificationContainer
);
